import React, { useEffect, useRef, useState } from 'react';
import { PDFDocument } from 'pdf-lib';
import { Loader } from 'semantic-ui-react';
import PropTypes from 'prop-types';
import FormService from '../../services/FormService';
import './PdfEditor.scss';

const getSaveFormName = (dealId, templateId) => {
  const timestamp = Date.now();
  return [dealId, templateId, timestamp, '.pdf'].join('');
};

const generateFormData = async ({ instance, templateId, dealId }) => {
  const arrayBuffer = await instance.exportPDF();
  const blob = new Blob([arrayBuffer], { type: 'application/pdf' });
  const formData = new FormData();
  formData.append('file', blob, getSaveFormName(dealId, templateId));
  formData.append('templateId', templateId);
  return formData;
};

const getAllAnnotations = async pdfInstance => {
  const pagesAnnotations = await Promise.all(
    Array.from({ length: pdfInstance.totalPageCount }).map((_, pageIndex) =>
      pdfInstance.getAnnotations(pageIndex),
    ),
  );
  return pagesAnnotations.flatMap(annotations => annotations.toArray());
};

const getAllSignatures = async instance => {
  const allAnnotations = await getAllAnnotations(instance);
  return allAnnotations.filter(annotation => {
    // when signatures are added from Papyrus, they do not have the designation 'isSignature'
    if (annotation.toJS().lines || annotation.toJS().isSignature) {
      return annotation.id;
    }
    return false;
  });
};

const autoSizeTextFormFields = async ({ PSPDFKit, instance }) => {
  const annotations = await getAllAnnotations(instance);
  const idToAnnotation = new Map(annotations.map(a => [a.id, a]));
  const idToAutoSizedAnnotation = new Map();

  // A form field also comes with annotations. The annotations are used for example
  // to render the field value at rest
  const fields = await instance.getFormFields();
  fields.forEach(field => {
    const { TextFormField } = PSPDFKit.FormFields;
    if (!(field instanceof TextFormField)) {
      return;
    }
    field.annotationIds.forEach(annotationId => {
      if (!idToAutoSizedAnnotation.has(annotationId)) {
        const updatedAnnotation = idToAnnotation
          .get(annotationId)
          .set('fontSize', 'auto');
        idToAutoSizedAnnotation.set(annotationId, updatedAnnotation);
      }
    });
  });

  const updatedAnnotations = Array.from(idToAutoSizedAnnotation.values());
  await instance.update(updatedAnnotations);
};

let hasSignatures = false;

function PdfEditorComponent({
  setCanCloseModal,
  dealId,
  templateId,
  onPdfSaved,
  document,
  completed,
}) {
  const containerRef = useRef(null);
  const [instance, setInstance] = useState();
  const [uploadError, setUploadError] = useState();
  const [isSaveSuccessful, setIsSaveSuccessful] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [warningDisplayed, setWarningDisplayed] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const onSave = async () => {
    if (isSaving) {
      return; // Disallow multiple clicks on Save Button
    }
    setUploadError();
    setIsSaveSuccessful(false);
    setIsSaving(true);
    setCanCloseModal(false);
    const formData = await generateFormData({ instance, templateId, dealId });

    try {
      await FormService.updateAlreadyFilledForm(dealId, formData);
      setIsSaveSuccessful(true);
    } catch (e) {
      setUploadError(e.message);
    } finally {
      // Things to do after it's saved
      onPdfSaved();
      setIsSaving(false);
      setCanCloseModal(true);
    }
  };

  const removeFormSignatures = async () => {
    const annotationIds = await getAllSignatures(instance);
    await instance.delete(annotationIds);
  };

  const confirm = async confirmed => {
    if (confirmed) {
      setWarningDisplayed(true);
      await removeFormSignatures();
    }
    setIsOpen(false);
  };

  const iframeRef = useRef(null);

  const onPrint = async pdfkitInstance => {
    await pdfkitInstance
      ?.exportPDF()
      .then(file => PDFDocument.load(file))
      .then(pdf => {
        pdf.getForm().flatten();
        return pdf.save();
      })
      .then(blobParts => {
        const objectUrl = URL.createObjectURL(
          new Blob([blobParts], { type: 'application/pdf' }),
        );
        iframeRef.current.src = objectUrl;
        iframeRef.current.onload = () => {
          iframeRef.current?.contentWindow?.print();
          window.URL.revokeObjectURL(objectUrl);
        };
      });
  };

  useEffect(() => {
    const container = containerRef.current;
    let PSPDFKit;
    (async function openPdfEditor() {
      PSPDFKit = await import('pspdfkit');

      let documentBuffer;
      if (document.includes('documents/v1')) {
        // If we are using the new private document instead of the legacy s3 public link
        // We download the whole content as blob and open in the PSPDFKit editor, instead of only providing a URL
        // Need to stream the content and not redirect when this is a backend URL otherwise we get a CORS error
        // This is due to S3 sending '*' back for allow origin header instead of the origin we specified on the request
        // which is not accepted by the browser's same-origin policy if we are sending cookies
        const pdfResponse = await fetch(`${document}?isStream=true`, {
          credentials: 'include',
        });
        documentBuffer = await pdfResponse.arrayBuffer();
      }
      try {
        const pdfKit = await PSPDFKit.load({
          // Container where PSPDFKit should be mounted.
          container,
          // The document to open.
          document: documentBuffer || document,
          // Use the public directory URL as a base URL. PSPDFKit will download its library assets from here.
          baseUrl: `${window.location.protocol}//${window.location.host}/${process.env.PUBLIC_URL}`,
          isEditableAnnotation: ann => !ann.strokeWidth, // Disable white rectangles used to redact forms to be editable/draggable
          licenseKey: process.env.REACT_APP_PSPDF_LICENSE_KEY,

          // Print preview hangs indefinitely when running locally with the default print mode
          // though it seems to work fine in a basic HTML file that only loads pspdfkit.
          // The default print mode also includes purple rectangles for the text fields, which
          // seems undesirable and is not what printing from iPad does.
          // https://pspdfkit.com/guides/web/print/print-modes/
          printMode: PSPDFKit.PrintMode.EXPORT_PDF,
        });
        setInstance(pdfKit);
        await autoSizeTextFormFields({ PSPDFKit, instance: pdfKit });

        // Here's why all of this code below is needed (so there's context on why we are doing this):
        // 1. When we get scanned PDFs from dealerships to use as template, they contain customer data (because of the way we obtain them: we request rendering of the forms in the CRMs and save)
        // 2. To cleanup the saved PDF, we (implementation specialist, etc) redact customer data printed in the image with PDF editor by creating white rectangles on top of them
        // 3. Then, everywhere we use the PDF, it will come with those white rectangles with it
        // 4. However, when viewing the already filled PDFs, the PSPDFKit Editor we are using here in Chairman puts those rectangles in the front of the editable fields. A weird behavior that is not present in the default Chrome browser PDF Viewer for example.
        // 5. The fix is to select the pdf iframe, get all editable annotation fields that are <div> (white rectangles are represented by <span>) and raise up their zIndex to 100 - so white rectangles stay in background and editable annotation fields on the foreground
        const bringEditableAnnotationFieldsToForeground = () =>
          window.frames?.[0]?.document
            ?.querySelectorAll('[data-annotation-id]')
            .forEach(data => {
              if (data.nodeName === 'DIV')
                data.parentElement.style.zIndex = 100;
            });

        // Call the function 4 times, because there's no good event to track if the annotations were rendered
        // The lower timing is for fast machines, while higher timing is for slow machines.
        // There is no significant performance impact or side-effect issues performing this operation multiple times
        setTimeout(bringEditableAnnotationFieldsToForeground, 500);
        setTimeout(bringEditableAnnotationFieldsToForeground, 1000);
        setTimeout(bringEditableAnnotationFieldsToForeground, 3000);
        setTimeout(bringEditableAnnotationFieldsToForeground, 10000);

        hasSignatures = false;

        // Override print icon to actually print form content
        // For some reason, this is not always the case by default (including on iframes)
        // Download seem more reliable, so download and flatten the file before printing
        pdfKit.setToolbarItems(items => {
          const indexOfPrint = items.findIndex(({ type }) => type === 'print');
          if (indexOfPrint > -1) {
            return [
              ...items.slice(0, indexOfPrint),
              {
                type: 'custom',
                id: 'print-pdf',
                title: 'Print',
                icon:
                  '<svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" focusable="false"><path d="M22.5 9v9a1.5 1.5 0 01-1.5 1.5h-2.5v-2h-13v2H3A1.5 1.5 0 011.5 18V9A1.5 1.5 0 013 7.5h2.5v2h13v-2H21A1.5 1.5 0 0122.5 9z" stroke="currentColor" stroke-miterlimit="10" fill="none"></path><path opacity=".25" d="M22 15v3a1 1 0 01-1 1h-2v-2H5v2H3a1 1 0 01-1-1v-3h20z" fill="currentColor"></path><path opacity=".5" d="M19.5 13.5a1 1 0 100-2 1 1 0 000 2z" fill="currentColor"></path><path d="M18.5 18v3.5h-13V18M5.5 9V2.5h13V9" stroke="currentColor" fill="none"></path></svg>',
                onPress: () => onPrint(pdfKit),
              },
              ...items.slice(indexOfPrint + 1),
            ];
          }
          return items;
        });
      } catch (e) {
        // PSPDFKit will show a nice user-friendly dialog if there are any issues loading (ie: bad pdf or network issues), we just need to make sure the exception doesn't bubble up - so it doesn't crash the whole React application
      }
    })();

    return () => PSPDFKit && PSPDFKit.unload(container);
  }, []);

  useEffect(() => {
    if (instance) {
      (async function addPressListener() {
        const signatures = await getAllSignatures(instance);
        hasSignatures = signatures.length > 0;

        if (hasSignatures) {
          // when any field is clicked on, open warning modal
          instance.addEventListener('annotations.press', () => {
            setIsOpen(true);
          });
        }
      })();
    }
  }, [instance]);

  return (
    <div className="pdf-editor-container">
      {uploadError && (
        <div>
          Something went wrong when Saving and Uploading ({uploadError}). Please
          try again
        </div>
      )}
      {instance && completed && (
        <div>
          <button
            className="btn btn-primary save-button"
            type="button"
            onClick={onSave}
          >
            Save Changes and Upload
          </button>

          <div className="saving-status">
            {isSaving && <Loader size="mini" content="Saving" active inline />}
            {isSaveSuccessful && <div>Saved Successfully!</div>}
          </div>

          {isOpen && !warningDisplayed && (
            <div className="p-modal-container">
              <div className="p-modal">
                <i
                  onClick={() => confirm(false)}
                  className="modal-close close icon"
                />
                <div className="p-modal-body">
                  Changing completed form will require a new customer signature,
                  do you want to proceed?
                </div>
                <div className="p-modal-actions">
                  <button
                    type="button"
                    className="btn btn-primary btn-large btn-large-default"
                    onClick={() => confirm(true)}
                  >
                    Yes
                  </button>
                  <button
                    type="button"
                    className="btn btn-default btn-large"
                    onClick={() => confirm(false)}
                  >
                    No
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
      <br />
      <div ref={containerRef} style={{ width: '100%', height: '95%' }} />
      <iframe ref={iframeRef} style={{ display: 'none' }} title="PDF form" />
    </div>
  );
}

PdfEditorComponent.propTypes = {
  setCanCloseModal: PropTypes.func,
  onPdfSaved: PropTypes.func,
  dealId: PropTypes.string,
  templateId: PropTypes.string,
  document: PropTypes.string,
  completed: PropTypes.bool,
};

export default PdfEditorComponent;
